import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { updateDisplayName, updateFavouriteTeam, updateAvatarPreset } from '../api/me'
import { loadLeagues } from '../api/leagues'
import { ApiError } from '../api/client'
import { changeAvatarPreset } from '../avatarChange'
import { mockProfile } from '../mockProfile'
import type { AvatarPresetId } from '../avatarPresets'
import type { League, QuestId } from '../types'
import type { TeamId } from '../teams'
import type { DataMode } from '../dataMode'
import { hasAnyDevTool } from '../devToolCapabilities'

export interface UseIdentityOptions {
  onToast: (message: string) => void
  /** The leagues payload resolves each member's name and avatar server-side, so the league rows
   *  only catch up once `/leagues` is refetched. */
  setLeagues: Dispatch<SetStateAction<League[]>>
}

export interface IdentityController {
  myUserId: string
  displayName: string
  accountEmail: string | null
  favouriteTeam: TeamId | null
  avatarPreset: AvatarPresetId | null
  /**
   * Single source of truth for "is the signed-in user the admin".
   *
   * Reported by the server via `/me`, not derived from `accountEmail` here. The list
   * of admins lives in `ADMIN_EMAILS`, and Bun's bundler puts no environment into the browser
   * bundle — a client that evaluated `isAdminEmail` itself saw only the placeholder fallback and
   * concluded the real admin was nobody, hiding the dev rail and the backoffice. False until
   * `/me` lands.
   */
  isAdmin: boolean
  setIsAdmin: Dispatch<SetStateAction<boolean>>
  /** Which world the server is running in; null until /me lands. */
  dataMode: DataMode | null
  setDataMode: Dispatch<SetStateAction<DataMode | null>>
  /**
   * Which quests this deployment runs, as reported by `/me`.
   *
   * Empty until it lands, not "all eight" — an unknown answer must render no quest section rather
   * than flash two cards and then remove them on a deployment that has quests switched off.
   */
  questsEnabled: QuestId[]
  setQuestsEnabled: Dispatch<SetStateAction<QuestId[]>>
  /**
   * The first gameweek this account may play, from `/me`; null when that is the season's first
   * gameweek, which is everyone who signed up before it started. Null until `/me` lands, so an
   * unknown answer shows no "you joined mid-week" line rather than flashing one at a user who
   * played the week perfectly well.
   */
  firstPlayableGameweek: number | null
  setFirstPlayableGameweek: Dispatch<SetStateAction<number | null>>
  /**
   * Admin, and at least one tool is available in the running mode.
   *
   * That second clause is now true in every mode — the rail always renders for the admin, and
   * *which* controls it shows is a per-tool question answered by `devToolCapabilities`. It used to
   * mean "admin AND not live", which hid the football status panel in the one mode it exists for.
   * It is still false until `/me` lands, so an unknown mode shows no rail rather than a wrong one.
   */
  canUseDevTools: boolean
  setMyUserId: Dispatch<SetStateAction<string>>
  setDisplayName: Dispatch<SetStateAction<string>>
  setAccountEmail: Dispatch<SetStateAction<string | null>>
  setFavouriteTeam: Dispatch<SetStateAction<TeamId | null>>
  setAvatarPreset: Dispatch<SetStateAction<AvatarPresetId | null>>
  /** Resolves to the server's own validation copy on a rejected name, for the field to show. */
  saveDisplayName: (name: string) => Promise<string | void>
  saveFavouriteTeam: (team: TeamId | null) => Promise<void>
  changeAvatarPreset: (preset: AvatarPresetId | null) => void
}

/**
 * Who the signed-in user is, and the three things they can change about
 * themselves. Each save writes server-side first and only then updates locally, except the avatar
 * — which updates optimistically because the picker shows it instantly (see `avatarChange`).
 */
export function useIdentity({ onToast, setLeagues }: UseIdentityOptions): IdentityController {
  const [myUserId, setMyUserId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  // A brand-new user has no server-persisted favourite team, so this cosmetic client default
  // stands in rather than an empty state the rest of the UI does not yet handle.
  const [favouriteTeam, setFavouriteTeam] = useState<TeamId | null>(mockProfile.favouriteTeam)
  const [avatarPreset, setAvatarPreset] = useState<AvatarPresetId | null>(null)
  const [dataMode, setDataMode] = useState<DataMode | null>(null)
  const [questsEnabled, setQuestsEnabled] = useState<QuestId[]>([])
  const [firstPlayableGameweek, setFirstPlayableGameweek] = useState<number | null>(null)

  return {
    myUserId,
    displayName,
    accountEmail,
    favouriteTeam,
    avatarPreset,
    isAdmin,
    setIsAdmin,
    dataMode,
    setDataMode,
    questsEnabled,
    setQuestsEnabled,
    firstPlayableGameweek,
    setFirstPlayableGameweek,
    canUseDevTools: isAdmin && hasAnyDevTool(dataMode),
    setMyUserId,
    setDisplayName,
    setAccountEmail,
    setFavouriteTeam,
    setAvatarPreset,

    saveDisplayName: async (name) => {
      try {
        const result = await updateDisplayName(name)
        setDisplayName(result.displayName)
        setLeagues(await loadLeagues())
        onToast('Display name updated.')
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.body &&
          typeof err.body === 'object' &&
          'error' in err.body
        ) {
          return (err.body as { error: string }).error
        }
        return 'Failed to update display name. Please try again.'
      }
    },

    saveFavouriteTeam: async (team) => {
      try {
        await updateFavouriteTeam(team)
        setFavouriteTeam(team)
        setLeagues(await loadLeagues())
      } catch {
        onToast('Failed to update favourite team. Please try again.')
      }
    },

    changeAvatarPreset: (preset) =>
      void changeAvatarPreset(preset, {
        previous: avatarPreset,
        setAvatarPreset,
        updateAvatarPreset,
        loadLeagues,
        setLeagues,
        setToastMessage: onToast,
      }),
  }
}
